import { useMemo } from 'react';
import type { SeriesPoint } from '@/api/observability';

interface SparklineProps {
  points: SeriesPoint[];
  /** Tailwind text-color class driving the stroke/fill (uses currentColor). */
  colorClass?: string;
  height?: number;
  ariaLabel?: string;
}

const VIEW_W = 100;

/**
 * Dependency-free inline SVG sparkline. Values are normalized into the viewBox;
 * null samples (no data for a step) break the line into segments so gaps are
 * visible rather than interpolated.
 */
const Sparkline = ({
  points,
  colorClass = 'text-blue-500 dark:text-blue-400',
  height = 28,
  ariaLabel,
}: SparklineProps) => {
  const { line, area, flat } = useMemo(() => {
    const valid = points.filter((p) => p.v !== null) as { t: number; v: number }[];
    if (valid.length === 0) return { line: '', area: '', flat: false };

    const values = valid.map((p) => p.v);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const n = points.length;
    const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * VIEW_W);
    // Invert y so larger values sit higher; pad 2px top/bottom.
    const y = (v: number) => height - 2 - ((v - min) / span) * (height - 4);

    // Build line segments, breaking on null samples.
    const segments: string[] = [];
    let current: string[] = [];
    points.forEach((p, i) => {
      if (p.v === null) {
        if (current.length) segments.push(current.join(' '));
        current = [];
        return;
      }
      current.push(`${current.length ? 'L' : 'M'}${x(i).toFixed(2)},${y(p.v).toFixed(2)}`);
    });
    if (current.length) segments.push(current.join(' '));

    // Area fills under the FIRST contiguous run only, so it never visually
    // bridges across missing-data gaps.
    const firstRun: { p: (typeof points)[number]; i: number }[] = [];
    let runStarted = false;
    for (let i = 0; i < points.length; i += 1) {
      const p = points[i];
      if (p.v !== null) {
        firstRun.push({ p, i });
        runStarted = true;
      } else if (runStarted) {
        break;
      }
    }
    let areaPath = '';
    if (firstRun.length > 1) {
      const head = firstRun.map(
        ({ p, i }, idx) =>
          `${idx === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(p.v as number).toFixed(2)}`,
      );
      const lastX = x(firstRun[firstRun.length - 1].i).toFixed(2);
      const firstX = x(firstRun[0].i).toFixed(2);
      areaPath = `${head.join(' ')} L${lastX},${height} L${firstX},${height} Z`;
    }

    return { line: segments.join(' '), area: areaPath, flat: span === 1 };
  }, [points, height]);

  if (!line) {
    return (
      <div
        className="flex h-7 items-center justify-center text-[10px] text-color-tertiary"
        aria-label={ariaLabel ? `${ariaLabel}: no data` : 'No data'}
      >
        No data
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${height}`}
      preserveAspectRatio="none"
      className={`h-7 w-full ${colorClass}`}
      role="img"
      aria-label={ariaLabel}
    >
      {area && <path d={area} fill="currentColor" opacity={0.12} stroke="none" />}
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={flat ? 1 : 1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

export default Sparkline;
